// apps/web/src/pages/admin/today/TodayTimelineView.tsx — Phase 5 plan 05-04 (B2·B).
//
// Renders the B2·B timeline view: hours on the left rail (12:00–22:00
// in 30-min steps), cards per hour. Empty slots render "frei".

import { format } from "date-fns";
import { useTranslation } from "react-i18next";
import { StatusTag, reservationRowClass } from "../shared/StatusTag";
import { SourceTag } from "../shared/SourceTag";
import type { ReservationStatusValue } from "../shared/StatusTag";
import type { ReservationSourceValue } from "../shared/SourceTag";
import type { TodayReservationItem } from "./TodayListView";

export function TodayTimelineView({
  items,
}: {
  items: TodayReservationItem[];
}) {
  const { t } = useTranslation();
  // Build the hour buckets from 12:00 to 22:00 in 30-min steps.
  const buckets: string[] = [];
  for (let h = 12; h <= 22; h++) {
    buckets.push(`${String(h).padStart(2, "0")}:00`);
    buckets.push(`${String(h).padStart(2, "0")}:30`);
  }

  const itemsByTime = new Map<string, TodayReservationItem[]>();
  for (const item of items) {
    const key = format(new Date(item.startsAt), "HH:mm");
    const list = itemsByTime.get(key) ?? [];
    list.push(item);
    itemsByTime.set(key, list);
  }

  return (
    <div
      data-testid="today-timeline"
      className="grid grid-cols-[60px_1fr] gap-2"
    >
      {buckets.map((bucket) => {
        const slotItems = itemsByTime.get(bucket) ?? [];
        return (
          <div
            key={bucket}
            className="contents"
            data-testid={`today-bucket-${bucket}`}
          >
            <div className="text-xs text-text/60 py-2">{bucket}</div>
            <div className="border-l border-divider pl-3 py-2">
              {slotItems.length === 0 ? (
                <span className="text-text/40 text-sm">frei</span>
              ) : (
                <ul className="flex flex-col gap-1">
                  {slotItems.map((item) => (
                    <TodayTimelineRow key={item.id} item={item} t={t} />
                  ))}
                </ul>
              )}
            </div>
          </div>
        );
      })}
    </div>
  );
}

function TodayTimelineRow({
  item,
  t,
}: {
  item: TodayReservationItem;
  t: ReturnType<typeof useTranslation>["t"];
}) {
  return (
    <li
      className={`text-sm flex items-center justify-between gap-2 ${reservationRowClass(item.status)}`}
      data-testid={`today-timeline-row-${item.id}`}
    >
      <span>
        <span className="font-medium">{item.guestName}</span>
        <span className="text-text/60"> · {item.partySize} Pers.</span>
      </span>
      <div className="flex gap-1">
        <StatusTag status={item.status} />
        <SourceTag source={item.source} />
      </div>
      <span className="text-xs text-text/40">
        {t("admin.today.reservationNumber", { number: item.reservationNumber })}
      </span>
    </li>
  );
}

export type { ReservationStatusValue, ReservationSourceValue };
